import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Loader2,
  ChevronDown,
  ChevronRight,
  Building2,
  X,
  Video,
  Users,
  BarChart3,
  Filter,
  ArrowUpDown,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { adminAPI, industriesAPI } from '@/services/api';
import { toast } from 'sonner';

export default function AdminIndustries() {
  const [industries, setIndustries] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedIndustries, setExpandedIndustries] = useState(new Set());
  const [showDialog, setShowDialog] = useState(false);
  const [editingIndustry, setEditingIndustry] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterType, setFilterType] = useState('all'); // all, main, sub
  const [sortBy, setSortBy] = useState('name'); // name, webinars, seminars
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: 'Briefcase',
    parent_id: '',
    is_active: true,
  });

  const iconOptions = [
    'Briefcase', 'Users', 'Heart', 'Building', 'Factory', 
    'BookOpen', 'Scale', 'Award', 'Shield', 'Globe'
  ];

  useEffect(() => {
    fetchIndustries();
    fetchAnalytics();
  }, []);

  const fetchIndustries = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getIndustries();
      setIndustries(response.data);
    } catch (error) {
      console.error('Error fetching industries:', error);
      toast.error('Failed to load industries');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await industriesAPI.getAnalytics();
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleOpenDialog = (industry = null) => {
    if (industry) {
      setEditingIndustry(industry);
      setFormData({
        name: industry.name || '',
        slug: industry.slug || '',
        description: industry.description || '',
        icon: industry.icon || 'Briefcase',
        parent_id: industry.parent_id || '',
        is_active: industry.is_active !== false,
      });
    } else {
      setEditingIndustry(null);
      setFormData({
        name: '',
        slug: '',
        description: '',
        icon: 'Briefcase',
        parent_id: '',
        is_active: true,
      });
    }
    setShowDialog(true);
  };

  const handleSaveIndustry = async () => {
    if (!formData.name) {
      toast.error('Please enter industry name');
      return;
    }

    try {
      setSaving(true);
      const data = {
        ...formData,
        slug: formData.slug || generateSlug(formData.name),
        parent_id: formData.parent_id || null,
      };

      if (editingIndustry) {
        await adminAPI.updateIndustry(editingIndustry.id, data);
        toast.success('Industry updated successfully');
      } else {
        await adminAPI.createIndustry(data);
        toast.success('Industry created successfully');
      }
      setShowDialog(false);
      fetchIndustries();
      fetchAnalytics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save industry');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteIndustry = async (industry) => {
    if (!window.confirm(`Are you sure you want to delete "${industry.name}"?`)) {
      return;
    }
    try {
      await adminAPI.deleteIndustry(industry.id);
      toast.success('Industry deleted successfully');
      fetchIndustries();
      fetchAnalytics();
    } catch (error) {
      toast.error('Failed to delete industry');
    }
  };

  const toggleExpand = (id) => {
    const newExpanded = new Set(expandedIndustries);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIndustries(newExpanded);
  };

  const expandAll = () => {
    const mainIds = industries.filter(i => !i.parent_id).map(i => i.id);
    setExpandedIndustries(new Set(mainIds));
  };

  const collapseAll = () => {
    setExpandedIndustries(new Set());
  };

  // Get main industries (no parent_id)
  const mainIndustries = industries.filter(i => !i.parent_id);
  
  // Group sub-industries by parent
  const subIndustriesByParent = {};
  industries.forEach(ind => {
    if (ind.parent_id) {
      if (!subIndustriesByParent[ind.parent_id]) {
        subIndustriesByParent[ind.parent_id] = [];
      }
      subIndustriesByParent[ind.parent_id].push(ind);
    }
  });

  // Filter and sort industries
  let displayIndustries = [...mainIndustries];
  
  if (searchTerm) {
    const term = searchTerm.toLowerCase();
    displayIndustries = displayIndustries.filter(ind => {
      const matchesMain = ind.name?.toLowerCase().includes(term);
      const matchesSub = subIndustriesByParent[ind.id]?.some(sub => 
        sub.name?.toLowerCase().includes(term)
      );
      return matchesMain || matchesSub;
    });
  }

  // Sort
  displayIndustries.sort((a, b) => {
    switch (sortBy) {
      case 'webinars':
        return (b.webinar_active_count || 0) - (a.webinar_active_count || 0);
      case 'seminars':
        return (b.seminar_active_count || 0) - (a.seminar_active_count || 0);
      case 'total':
        return (b.total_content || 0) - (a.total_content || 0);
      default:
        return (a.name || '').localeCompare(b.name || '');
    }
  });

  return (
    <div className="space-y-6" data-testid="admin-industries">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-navy-700">Industry Management</h2>
          <p className="text-slate-500">Manage industries and analyze content distribution</p>
        </div>
        <Button 
          onClick={() => handleOpenDialog()}
          className="bg-navy-600 hover:bg-navy-700 rounded-xl"
          data-testid="add-industry-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Industry
        </Button>
      </div>

      {/* Summary Cards */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-slate-200 rounded-xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy-700">{analytics.summary?.total_main_industries || 0}</p>
                  <p className="text-xs text-slate-500">Main Industries</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 rounded-xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy-700">{analytics.summary?.total_sub_industries || 0}</p>
                  <p className="text-xs text-slate-500">Sub-Industries</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 rounded-xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Video className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy-700">{analytics.summary?.total_webinars_active || 0}</p>
                  <p className="text-xs text-slate-500">Active Webinars</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 rounded-xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy-700">{analytics.summary?.total_seminars_active || 0}</p>
                  <p className="text-xs text-slate-500">Active Seminars</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filters */}
      <Card className="border-slate-200 rounded-xl">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search industries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-lg"
              />
            </div>
            <div className="flex gap-2">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-40">
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="webinars">Webinars</SelectItem>
                  <SelectItem value="seminars">Seminars</SelectItem>
                  <SelectItem value="total">Total Content</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={expandAll}>
                Expand All
              </Button>
              <Button variant="outline" size="sm" onClick={collapseAll}>
                Collapse
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Industries Table */}
      <Card className="border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-4 font-semibold text-slate-700 text-sm">Industry</th>
                <th className="text-center p-4 font-semibold text-slate-700 text-sm w-24">Status</th>
                <th className="text-center p-4 font-semibold text-slate-700 text-sm w-28">
                  <div className="flex items-center justify-center gap-1">
                    <Video className="w-4 h-4" />
                    Webinars
                  </div>
                </th>
                <th className="text-center p-4 font-semibold text-slate-700 text-sm w-28">
                  <div className="flex items-center justify-center gap-1">
                    <Users className="w-4 h-4" />
                    Seminars
                  </div>
                </th>
                <th className="text-center p-4 font-semibold text-slate-700 text-sm w-24">Subs</th>
                <th className="text-right p-4 font-semibold text-slate-700 text-sm w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-navy-500 mx-auto" />
                  </td>
                </tr>
              ) : displayIndustries.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-slate-500">
                    No industries found
                  </td>
                </tr>
              ) : (
                displayIndustries.map((industry) => {
                  const subs = subIndustriesByParent[industry.id] || [];
                  const isExpanded = expandedIndustries.has(industry.id);
                  
                  return (
                    <React.Fragment key={industry.id}>
                      {/* Main Industry Row */}
                      <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => toggleExpand(industry.id)}
                              className="p-1 hover:bg-slate-200 rounded"
                              disabled={subs.length === 0}
                            >
                              {subs.length > 0 ? (
                                isExpanded ? (
                                  <ChevronDown className="w-4 h-4 text-slate-500" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-slate-500" />
                                )
                              ) : (
                                <div className="w-4 h-4" />
                              )}
                            </button>
                            <div className="w-10 h-10 rounded-lg bg-navy-100 flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-navy-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-navy-700">{industry.name}</p>
                              {industry.description && (
                                <p className="text-xs text-slate-500 line-clamp-1 max-w-xs">
                                  {industry.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          {industry.is_active !== false ? (
                            <Badge className="bg-green-100 text-green-700 border-0">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Active
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700 border-0">
                              <XCircle className="w-3 h-3 mr-1" />
                              Inactive
                            </Badge>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex flex-col items-center">
                            <span className="text-lg font-bold text-navy-700">
                              {industry.webinar_active_count || 0}
                            </span>
                            <span className="text-xs text-slate-400">
                              / {industry.webinar_count || 0} total
                            </span>
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex flex-col items-center">
                            <span className="text-lg font-bold text-navy-700">
                              {industry.seminar_active_count || 0}
                            </span>
                            <span className="text-xs text-slate-400">
                              / {industry.seminar_count || 0} total
                            </span>
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          <Badge variant="secondary" className="text-xs">
                            {subs.length}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenDialog(industry)}
                              className="h-8 w-8"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteIndustry(industry)}
                              className="h-8 w-8 text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      
                      {/* Sub-Industries Rows */}
                      {isExpanded && subs.map((sub) => (
                        <tr key={sub.id} className="border-b border-slate-100 bg-slate-50/50 hover:bg-slate-100/50 transition-colors">
                          <td className="p-4 pl-16">
                            <div className="flex items-center gap-3">
                              <div className="w-2 h-2 rounded-full bg-slate-300" />
                              <div>
                                <p className="font-medium text-slate-700">{sub.name}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 text-center">
                            {sub.is_active !== false ? (
                              <Badge className="bg-green-100 text-green-700 border-0 text-xs">
                                Active
                              </Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-700 border-0 text-xs">
                                Inactive
                              </Badge>
                            )}
                          </td>
                          <td className="p-4 text-center">
                            <span className="font-semibold text-slate-700">
                              {sub.webinar_active_count || 0}
                            </span>
                            <span className="text-xs text-slate-400 ml-1">
                              / {sub.webinar_count || 0}
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            <span className="font-semibold text-slate-700">
                              {sub.seminar_active_count || 0}
                            </span>
                            <span className="text-xs text-slate-400 ml-1">
                              / {sub.seminar_count || 0}
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            <span className="text-slate-400">-</span>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleOpenDialog(sub)}
                                className="h-8 w-8"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteIndustry(sub)}
                                className="h-8 w-8 text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add/Edit Industry Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingIndustry ? 'Edit Industry' : 'Add New Industry'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  name: e.target.value,
                  slug: generateSlug(e.target.value)
                })}
                placeholder="Industry name"
              />
            </div>

            <div className="space-y-2">
              <Label>Parent Industry (leave empty for main industry)</Label>
              <Select 
                value={formData.parent_id || 'none'} 
                onValueChange={(val) => setFormData({ ...formData, parent_id: val === 'none' ? '' : val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select parent industry" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (Main Industry)</SelectItem>
                  {mainIndustries.map((ind) => (
                    <SelectItem key={ind.id} value={ind.id}>
                      {ind.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Slug</Label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="industry-url-slug"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the industry"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select 
                value={formData.is_active ? 'active' : 'inactive'} 
                onValueChange={(val) => setFormData({ ...formData, is_active: val === 'active' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Icon</Label>
              <div className="flex flex-wrap gap-2">
                {iconOptions.map((icon) => (
                  <Button
                    key={icon}
                    type="button"
                    variant={formData.icon === icon ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFormData({ ...formData, icon })}
                    className={formData.icon === icon ? 'bg-navy-600' : ''}
                  >
                    {icon}
                  </Button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveIndustry}
                disabled={saving}
                className="bg-navy-600 hover:bg-navy-700"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingIndustry ? 'Save Changes' : 'Create Industry'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
